export type CurrencyDisplayMode = 'off' | 'symbol' | 'postfix';

const resolveCurrencySymbol = (currencyCode: string, locale = 'en-US'): string => {
    const normalizedCurrencyCode = String(currencyCode || '').trim().toUpperCase();
    if (normalizedCurrencyCode === '') {
        return '';
    }

    try {
        const formatter = new Intl.NumberFormat(locale, {
            style: 'currency',
            currency: normalizedCurrencyCode,
            currencyDisplay: 'narrowSymbol',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        });
        const currencyPart = formatter.formatToParts(0).find((part) => part.type === 'currency');
        if (currencyPart && String(currencyPart.value || '').trim() !== '') {
            return String(currencyPart.value).trim();
        }
    } catch {
        // Fall through to a plain code fallback when the runtime cannot infer a symbol.
    }

    return normalizedCurrencyCode;
};

export const normalizeCurrencyDisplayMode = (value: unknown): CurrencyDisplayMode => {
    if (value === true) {
        return 'postfix';
    }

    if (value === false || value === null || value === undefined) {
        return 'off';
    }

    const normalizedValue = String(value).trim().toLowerCase();
    if (normalizedValue === 'postfix' || normalizedValue === 'symbol' || normalizedValue === 'off') {
        return normalizedValue;
    }

    if (['1', 'true', 'yes'].includes(normalizedValue)) {
        return 'postfix';
    }

    return 'off';
};

export interface FormattedMoneyParts {
    amount: string;
    currencyCode: string;
    formatted: string;
}

export const formatMoneyPartsWithCurrencyCode = (
    amount: string | number | undefined,
    currencyCode: string | undefined,
    currencyDisplayMode: CurrencyDisplayMode | boolean | string | undefined,
    currencySymbol?: string,
    locale = 'en-US',
): FormattedMoneyParts => {
    const normalizedCurrencyCode = String(currencyCode || '').trim().toUpperCase() || 'USD';
    const numericAmount = typeof amount === 'number' ? amount : parseFloat(String(amount || '0'));
    const safeAmount = Number.isFinite(numericAmount) ? numericAmount : 0;
    const absAmount = Math.abs(safeAmount);
    const sign = safeAmount < 0 ? '-' : '';
    const normalizedCurrencySymbol = String(currencySymbol || '').trim() || resolveCurrencySymbol(normalizedCurrencyCode, locale);
    const normalizedDisplayMode = normalizeCurrencyDisplayMode(currencyDisplayMode);

    const formattedAmount = new Intl.NumberFormat(locale, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(absAmount);
    const amountWithSymbol = normalizedDisplayMode === 'off'
        ? `${sign}${formattedAmount}`
        : `${sign}${normalizedCurrencySymbol}${formattedAmount}`;
    const resolvedCurrencyCode = normalizedDisplayMode === 'postfix' ? normalizedCurrencyCode : '';

    return {
        amount: amountWithSymbol,
        currencyCode: resolvedCurrencyCode,
        formatted: resolvedCurrencyCode !== ''
            ? `${amountWithSymbol} ${resolvedCurrencyCode}`
            : amountWithSymbol,
    };
};

export const formatMoneyWithCurrencyCode = (
    amount: string | number | undefined,
    currencyCode: string | undefined,
    currencyDisplayMode: CurrencyDisplayMode | boolean | string | undefined,
    currencySymbol?: string,
    locale = 'en-US',
): string => {
    return formatMoneyPartsWithCurrencyCode(
        amount,
        currencyCode,
        currencyDisplayMode,
        currencySymbol,
        locale
    ).formatted;
};
